import {isBefore} from 'date-fns';
import {DataStatus, Form, FormField, ValidationErrors} from 'platform/components';
import {Grid, GridItem} from 'platform/foundation';

import {UseFormReset, UseFormReturn} from 'react-hook-form';

import {useGetSupplierOrderVariantsQuery, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {
  DateRangeField,
  formatExternalFiltersDate,
  getOptionsFromWarehouses,
  RangeField,
} from '@dms/shared';
import {getOptionsFromSupplierOrderVariants} from '@dms/warehouse/shared';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

type AddToSupplierOrderExternalFilterForm = Partial<{
  warehouseId: string;
  orderVariantId: string;
  created: DateRangeField;
}>;

type AddToSupplierOrderExternalFilterProps = {warehouseId: string} & FormToDataGridConnectorProps &
  TestIdProps;

export function AddToSupplierOrderExternalFilter(props: AddToSupplierOrderExternalFilterProps) {
  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery();

  const {
    data: supplierOrderVariants,
    isLoading: isSupplierOrderVariantsLoading,
    isError: isSupplierOrderVariantsError,
  } = useGetSupplierOrderVariantsQuery({});

  const isLoading = isWarehousesLoading || isSupplierOrderVariantsLoading;

  const isError = isWarehousesError || isSupplierOrderVariantsError;

  const values = {
    ...props.values,
    warehouseId: props.warehouseId,
  };

  const handleChange = (
    values: AddToSupplierOrderExternalFilterForm,
    _setErrors: (errors: ValidationErrors | null) => void,
    _reset: UseFormReset<AddToSupplierOrderExternalFilterForm>,
    formApi: UseFormReturn<AddToSupplierOrderExternalFilterForm>
  ) => {
    const created = formApi.getValues('created');

    const isCreatedInvalid =
      created?.to && created?.from && isBefore(parseDate(created.to), parseDate(created.from));

    if (isCreatedInvalid) {
      formApi.setError('created.from', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
      formApi.setError('created.to', {
        message: i18n.t('entity.warehouse.labels.wrongDateRangeFormat'),
      });
    } else {
      formApi.clearErrors('created.from');
      formApi.clearErrors('created.to');
    }

    const isFormInvalid = isCreatedInvalid;

    if (isFormInvalid) {
      return;
    }

    const formattedCreated = {
      from: formatExternalFiltersDate(values.created?.from),
      to: formatExternalFiltersDate(values.created?.to),
    };

    props.onChange({
      ...values,
      created: formattedCreated,
    });
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<AddToSupplierOrderExternalFilterForm>
        values={values}
        defaultValues={{}}
        onChange={handleChange}
      >
        {(control) => {
          return (
            <Grid columns={4}>
              <GridItem span={1}>
                <FormField
                  isDisabled
                  control={control}
                  name="warehouseId"
                  type="choice"
                  label={i18n.t('entity.warehouse.labels.warehouse')}
                  options={getOptionsFromWarehouses(warehouses)}
                  data-testid={suffixTestId('inputs.requestingWarehouseId', props)}
                />
              </GridItem>

              <GridItem span={1}>
                <FormField
                  control={control}
                  type="choice"
                  name="orderVariantId"
                  label={i18n.t('entity.warehouse.labels.supplierOrderVariant')}
                  options={getOptionsFromSupplierOrderVariants(supplierOrderVariants)}
                  data-testid={suffixTestId('inputs.orderVariantId', props)}
                />
              </GridItem>

              <GridItem span={2}>
                <RangeField
                  control={control}
                  type="date"
                  name="created"
                  labelFrom={i18n.t('general.labels.createdFrom')}
                  labelTo={i18n.t('general.labels.createdTo')}
                  data-testid={suffixTestId('inputs.created', props)}
                />
              </GridItem>
            </Grid>
          );
        }}
      </Form>
    </DataStatus>
  );
}
